import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Attendee } from "./types";
import type { AttendeeType } from "./colors";

const CONFERENCE_SLUG = "regional-conference-2026";

function fullName(a?: string | null, b?: string | null) {
  return `${(a || "").trim()} ${(b || "").trim()}`.trim();
}

function fullName3(a?: string | null, b?: string | null, c?: string | null) {
  return `${(a || "").trim()} ${(b || "").trim()} ${(c || "").trim()}`.replace(/\s+/g, " ").trim();
}

function categoryLabel(raw?: string | null) {
  const s = (raw || "individual").trim();
  return s ? s.replace(/^\w/, (c: string) => c.toUpperCase()) : "Individual";
}

function relationshipOrder(m: any) {
  const rel = (m.relationship || "").toLowerCase();
  const type = inferMemberType(m);
  if (["primary", "self", "head", "registrant"].some((v) => rel.includes(v))) return 0;
  if (rel.includes("spouse")) return 1;
  if (type === "adult") return 2;
  if (type === "student") return 3;
  if (type === "teen") return 4;
  return 5;
}

function inferMemberType(m: any): AttendeeType {
  const t = (m.attendee_type || "").toLowerCase();
  if (["speaker", "organizer", "volunteer", "guest"].includes(t)) return t as AttendeeType;
  const cat = (m.child_category || "").toLowerCase();
  const ag = (m.age_group || "").toLowerCase();
  const age: number | null = typeof m.age === "number" ? m.age : null;
  if (ag === "teen" || (age !== null && age >= 13 && age <= 17)) return "teen";
  if (cat.includes("0") && cat.includes("5")) return "child_0_5";
  if (cat.includes("6") && (cat.includes("12") || cat.includes("11"))) return "child_6_12";
  if (ag.startsWith("child")) {
    if (age !== null && age <= 5) return "child_0_5";
    return "child_6_12";
  }
  if (ag === "adult") {
    if ((m.relationship || "").toLowerCase().includes("student")) return "student";
    return "adult";
  }
  if (age !== null) {
    if (age <= 5) return "child_0_5";
    if (age <= 12) return "child_6_12";
    if (age <= 17) return "teen";
    return "adult";
  }
  return "adult";
}

function inferPrimaryType(r: any): AttendeeType {
  if (r.is_student) return "student";
  return "adult";
}

export function useAttendees(regionRestriction: string | null) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [regions, setRegions] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = async (showLoader: boolean) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);
    try {
      let q = supabase
        .from("conference_registrations")
        .select("id, first_name, last_name, region, city, state, category, is_student, payment_status, spouse_first_name, spouse_last_name")
        .eq("conference_slug", CONFERENCE_SLUG);
      if (regionRestriction) {
        // Match region variants ("Southern", "Southern Region", trailing spaces)
        // so typos in registrations don't drop attendees from the badge roster.
        q = q.ilike("region", `${regionRestriction}%`);
      }
      const { data: regs, error: regErr } = await q;
      if (regErr) throw regErr;
      const regsSafe = (regs as any[]) || [];

      const ids = regsSafe.map((r) => r.id);
      const regsById = new Map(regsSafe.map((r) => [r.id, r]));
      let members: any[] = [];
      if (ids.length) {
        const { data } = await supabase
          .from("registration_members")
          .select("id, registration_id, first_name, middle_name, last_name, relationship, age_group, age, child_category, attendee_type")
          .in("registration_id", ids);
        members = (data as any[]) || [];
      }

      const memberBacked = members
        .map((m) => {
          const r = regsById.get(m.registration_id);
          if (!r) return null;
          const familyLast = (r.last_name || r.spouse_last_name || r.first_name || "").trim().toLowerCase();
          return {
            key: `${m.registration_id}:${m.id}`,
            registrationId: m.registration_id,
            memberId: m.id,
            fullName: fullName3(m.first_name, m.middle_name, m.last_name),
            region: r.region || "",
            city: r.city || "",
            registrationCategory: categoryLabel(r.category),
            attendeeType: inferMemberType(m),
            paymentStatus: r.payment_status || "pending",
            registrationStatus: "active",
            _familyKey: `${familyLast}|${r.id}`,
            _order: relationshipOrder(m),
          } as Attendee & { _familyKey: string; _order: number };
        })
        .filter((a): a is Attendee & { _familyKey: string; _order: number } => !!a && !!a.fullName.trim());

      // The registration_members table is the live roster used by reports and
      // has one row per person, so it is the source of truth for badge count.
      // Fallback only covers older/imported registrations that lack member rows.
      const memberRegIds = new Set(memberBacked.map((a) => a.registrationId));
      const fallback: (Attendee & { _familyKey: string; _order: number })[] = [];
      for (const r of regsSafe) {
        if (memberRegIds.has(r.id)) continue;
        const familyLast = (r.last_name || r.spouse_last_name || r.first_name || "").trim().toLowerCase();
        const familyKey = `${familyLast}|${r.id}`;
        const cat = categoryLabel(r.category);
        const primaryName = fullName(r.first_name, r.last_name);
        if (primaryName) {
          fallback.push({
            key: `${r.id}:primary`,
            registrationId: r.id,
            memberId: null,
            fullName: primaryName,
            region: r.region || "",
            city: r.city || "",
            registrationCategory: cat,
            attendeeType: inferPrimaryType(r),
            paymentStatus: r.payment_status || "pending",
            registrationStatus: "active",
            _familyKey: familyKey,
            _order: 0,
          });
        }
        const spouseName = fullName(r.spouse_first_name, r.spouse_last_name);
        if (spouseName) {
          fallback.push({
            key: `${r.id}:spouse`,
            registrationId: r.id,
            memberId: null,
            fullName: spouseName,
            region: r.region || "",
            city: r.city || "",
            registrationCategory: cat,
            attendeeType: "adult",
            paymentStatus: r.payment_status || "pending",
            registrationStatus: "active",
            _familyKey: familyKey,
            _order: 1,
          });
        }
      }

      const out = [...memberBacked, ...fallback]
        .sort((a, b) => {
          const fk = a._familyKey.localeCompare(b._familyKey);
          if (fk !== 0) return fk;
          if (a._order !== b._order) return a._order - b._order;
          return a.fullName.localeCompare(b.fullName);
        })
        .map(({ _familyKey, _order, ...rest }) => rest);

      setAttendees(out);
      setRegions(Array.from(new Set(regsSafe.map((r) => r.region).filter(Boolean))) as string[]);
    } catch (e: any) {
      setError(e.message || "Failed to load attendees");
    } finally {
      if (showLoader) setLoading(false);
      else setRefreshing(false);
    }
  };

  useEffect(() => {
    load(true);

    // Realtime: pick up new registrations & member additions automatically.
    const channel = supabase
      .channel("badge-generator-live")
      .on("postgres_changes", { event: "*", schema: "public", table: "conference_registrations" }, () => load(false))
      .on("postgres_changes", { event: "*", schema: "public", table: "registration_members" }, () => load(false))
      .on("postgres_changes", { event: "*", schema: "public", table: "registration_cancellation_requests" }, () => load(false))
      .subscribe();

    // Fallback poll every 20s in case realtime hasn't propagated yet.
    const poll = setInterval(() => load(false), 20000);

    // Refresh whenever the tab is refocused so the operator always sees the latest roster.
    const onFocus = () => load(false);
    window.addEventListener("focus", onFocus);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(poll);
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionRestriction]);

  const refresh = () => load(false);

  return { loading, refreshing, attendees, setAttendees, regions, error, refresh };
}
